// Tipos compartidos entre el Worker y el navegador.
// Los campos van en snake_case porque son el formato JSON de la API y de la descarga.

import type { Correction } from "./reliability";
import type { PlainSummary } from "./summary";

// Respuestas posibles de un paper a la pregunta (claves del choice `answer`)
export const ANSWER_LABELS = {
  yes: "Sí",
  no: "No",
  partial: "Parcial / condicional",
  no_effect: "Sin efecto significativo",
  inconclusive: "No concluyente",
  not_addressed: "No responde",
} as const;
export type AnswerKey = keyof typeof ANSWER_LABELS;

export const DESIGN_LABELS = {
  meta_analysis: "Meta-análisis / revisión sistemática",
  rct: "Ensayo controlado aleatorizado",
  quasi_experimental: "Cuasi-experimento / experimento natural",
  cohort: "Cohorte",
  case_control: "Casos y controles",
  observational: "Observacional (otros)",
  cross_sectional: "Transversal",
  modelling: "Modelización / simulación",
  measurement: "Experimento u observación (ciencias físicas)",
  animal_in_vitro: "Animal / in vitro",
  narrative_review: "Revisión narrativa / opinión",
  qualitative: "Cualitativo",
  other: "Otro",
} as const;

// Disciplina de la pregunta (la detecta Jev al preparar la búsqueda)
export const FIELD_LABELS = {
  health: "Salud, medicina, nutrición o conducta humana",
  life: "Biología, ecología o animales",
  social: "Economía, educación, sociología o políticas públicas",
  physical: "Ciencias físicas, ingeniería, energía o clima",
  other: "Otra",
} as const;
export type FieldKey = keyof typeof FIELD_LABELS;
export type DesignKey = keyof typeof DESIGN_LABELS;

export type SourceName = "europepmc" | "openalex" | "semanticscholar" | "arxiv";
export const SOURCES: SourceName[] = ["europepmc", "openalex", "semanticscholar", "arxiv"];
// arXiv solo aporta en estas disciplinas (física, matemáticas, informática…)
export const ARXIV_FIELDS: ReadonlySet<FieldKey> = new Set(["physical", "other"]);
// Fuentes que no necesitan clave (Semantic Scholar sin clave casi siempre da 429)
export const DEFAULT_SOURCES: SourceName[] = ["europepmc", "openalex"];

/** Órdenes que admite cada fuente (Semantic Scholar solo relevancia en páginas pequeñas). */
/** Pausa mínima entre páginas de una misma fuente (Semantic Scholar: 1 petición/s con clave). */
export const SOURCE_MIN_INTERVAL_MS: Partial<Record<SourceName, number>> = { semanticscholar: 1100, arxiv: 3100 };

/**
 * Páginas máximas por fuente y orden en la fase rápida (#47). Cada página de OpenAlex cuesta
 * 0,001 $ de un presupuesto de 1 $/día y trae 15 papers (límite de CPU): pidiendo el límite
 * completo (100 por orden) eran ~14 llamadas por búsqueda; así son 3 (30 por relevancia y 15 por
 * citas). Europe PMC, gratis, aporta el resto; la exhaustiva no tiene este tope.
 */
export const SOURCE_QUICK_PAGES: Partial<Record<SourceName, Partial<Record<SortOrder, number>>>> = {
  openalex: { default: 2, cited: 1 },
};

export const SOURCE_SORTS: Record<SourceName, SortOrder[]> = {
  europepmc: ["cited", "default"],
  openalex: ["cited", "default"],
  semanticscholar: ["default"],
  arxiv: ["default"],
};

export interface Paper {
  source_id: string;
  // Fuente de la que viene (Europe PMC: biomedicina; OpenAlex y Semantic Scholar: todas las disciplinas)
  source?: SourceName;
  // Todas las fuentes en las que aparece (tras deduplicar)
  sources?: SourceName[];
  cited_by_count?: number | null;
  // Cómo se llegó a él si no fue por la búsqueda (p. ej. citado por una revisión sistemática)
  via?: string | null;
  title: string;
  abstract: string;
  doi?: string | null;
  pmid?: string | null;
  pmcid?: string | null;
  year?: number | null;
  journal?: string | null;
  authors?: string | null;
  publication_types: string[];
  // Temas indexados (MeSH, palabras clave): se usan para ampliar la búsqueda exhaustiva
  keywords?: string[];
  url?: string | null;
  // Versión legal gratuita (acceso abierto): Europe PMC o OpenAlex (datos de Unpaywall)
  oa_url?: string | null;
  // Licencia del artículo (p. ej. «cc by»): con licencia abierta se muestra el abstract completo (#21)
  license?: string | null;
  // El abstract guardado es solo un extracto (licencia no abierta)
  abstract_is_excerpt?: boolean;
}

/** DOI sin prefijo y en minúsculas ("10.1000/abc"), o null. */
export function normalizeDoi(doi: string | null | undefined): string | null {
  const d = doi?.trim().toLowerCase().replace(/^(https?:\/\/(dx\.)?doi\.org\/|doi:)/, "");
  return d || null;
}

export function paperUrl(p: Pick<Paper, "doi" | "pmid" | "pmcid">): string | null {
  if (p.doi) return `https://doi.org/${p.doi}`;
  if (p.pmid) return `https://pubmed.ncbi.nlm.nih.gov/${p.pmid}/`;
  if (p.pmcid) return `https://europepmc.org/article/PMC/${p.pmcid}`;
  return null;
}

export interface Classification {
  relevance: number;
  raw_answer: string;
  answer_probabilities: Record<string, number>;
  answer_confidence: number;
  design: string;
  design_confidence: number;
  design_probabilities: Record<string, number>;
  humans: number;
  // Respuesta final tras aplicar el umbral de relevancia
  answer: string;
  uncertain: boolean;
  // Peso en el veredicto (diseño × humanos × relevancia × confianza); 0 si no responde
  weight: number;
}

export interface PaperResult {
  paper: Paper;
  classification: Classification | null;
  error: string | null;
}

/** Qué dicen las revisiones sistemáticas / meta-análisis relevantes. */
export interface ReviewsSummary {
  total: number;
  counts: Record<string, number>;
}

export type Side = "support" | "neutral" | "against";

export interface Verdict {
  label: string; // yes, likely_yes, mixed, no_evidence, no_effect, likely_no, no, insufficient
  headline: string;
  explanation: string;
  certainty: string; // alta, moderada, baja, muy baja
  // -1 (no) … +1 (sí), media ponderada de las respuestas
  score: number;
  // Peso relativo a favor / neutral / en contra
  shares: Record<Side, number>;
  // Suma de pesos: cuánta evidencia "equivalente a meta-análisis" hay
  effective_n: number;
  n_relevant: number;
  // Cuántos de los relevantes son revisiones sistemáticas o ensayos aleatorizados
  n_strong?: number;
  reviews: ReviewsSummary;
  // Correcciones de fiabilidad aplicadas (retractados, grupos de autores, desempate por revisiones)
  corrections: Correction[];
}

export interface AnalysisResult {
  question: string;
  // Traducción al inglés, si la pregunta no estaba en inglés
  question_en: string | null;
  query: string;
  backend: string;
  total_found: number;
  results: PaperResult[];
  // Conteo de respuestas de los papers clasificados
  summary: Record<AnswerKey, number>;
  verdict: Verdict | null;
  // Resumen en lenguaje llano del veredicto, citando los papers clave
  plain_summary: PlainSummary | null;
  // Mensajes para el usuario (traducción, consulta generada, sin resultados…)
  notices: string[];
  // Fase de la búsqueda: rápida (papers priorizados) o exhaustiva (toda la consulta)
  phase: "quick" | "exhaustive";
  // La exhaustiva ha recorrido todos los resultados de la consulta
  exhaustive_done: boolean;
  // La exhaustiva se detuvo en el tope de papers
  limit_reached: boolean;
  // Id de la búsqueda guardada, para releerla (null sin base de datos), y slug de su página
  search_id: string | null;
  slug?: string | null;
  // El resultado viene de una búsqueda guardada (caché)
  cached: boolean;
  // La pregunta es de salud o conducta humana (afecta a los matices del resumen)
  human_focus?: boolean;
}

// ---- Contrato de la API del Worker

export interface PlanRequest {
  // true: no reutilizar una búsqueda guardada («Actualizar»)
  refresh?: boolean;
  // Token de Turnstile (#14): se exige para empezar una búsqueda nueva
  turnstile_token?: string;
  question: string;
  query?: string;
  // Traducción hecha en el navegador (MyMemory con la IP del usuario)
  question_en?: string;
}
export interface Plan {
  question: string;
  question_en: string | null;
  // Traductor que produjo `question_en` (null si no hizo falta o fallaron todos)
  translator: "mymemory-browser" | "workers-ai" | "mymemory" | null;
  // Consultas de más estricta a más relajada
  queries: string[];
  // Cláusula OR de cada concepto de la pregunta (con sus sinónimos), en orden
  groups: string[];
  // Disciplina (null si no se pudo detectar) y si trata de salud/conducta humana
  field: FieldKey | null;
  human_focus: boolean;
  notices: string[];
  // Id de la búsqueda guardada (null sin base de datos) y slug de su página (#17)
  search_id?: string | null;
  slug?: string | null;
  // Si es una búsqueda guardada que se reutiliza (caché): cuándo se hizo y cuánto analizó
  cached?: { phase: "quick" | "exhaustive"; n: number; created_at: number } | null;
}

/** Una búsqueda guardada (GET /api/searches/:id). */
export interface StoredSearch {
  id: string;
  question: string;
  question_en: string | null;
  plan: Plan;
  phase: "quick" | "exhaustive";
  created_at: number;
  updated_at: number;
  results: PaperResult[];
}

export interface ExpandRequest {
  question: string;
  question_en?: string | null;
  // Temas frecuentes en los papers relevantes, candidatos a ampliar la búsqueda
  topics: string[];
  // Cláusulas de la consulta (Plan.groups): cada tema aceptado se asigna a una
  groups?: string[];
}
export interface ExpandResponse {
  accepted: string[];
  // Tema → índice del grupo al que pertenece (si se enviaron los grupos)
  // (con los términos de entrada MeSH del tema, que se añaden igual)
  assigned?: { topic: string; group: number; synonyms?: string[] }[];
}

export type SortOrder = "default" | "cited";
export interface SearchRequest {
  query: string;
  source?: SourceName;
  sort: SortOrder;
  cursor?: string;
  page_size?: number;
  title_abs_only?: boolean;
}
export interface SearchPage {
  papers: Paper[];
  hit_count: number;
  // null cuando no hay más páginas
  next_cursor: string | null;
  // Aviso para el usuario (p. ej. OpenAlex sin clave por presupuesto agotado)
  notice?: string;
}

export interface ReferencesRequest {
  // Revisiones cuyas referencias se quieren: DOIs (se resuelven en OpenAlex) o IDs de OpenAlex
  dois?: string[];
  openalex_ids?: string[];
  cursor?: string;
}
export interface ReferencesPage extends SearchPage {
  // IDs de OpenAlex resueltos, para pedir las páginas siguientes sin volver a resolver
  openalex_ids: string[];
}

export interface ClassifyRequest {
  question: string;
  question_en?: string | null;
  // Penalizar estudios no hechos en humanos (preguntas de salud); por defecto sí
  human_focus?: boolean;
  // Disciplina de la pregunta: en ciencias físicas se usan preguntas adaptadas (#61)
  field?: FieldKey | null;
  papers: Paper[];
  // Búsqueda a la que pertenecen (se guardan con ella) y su fase
  search_id?: string | null;
  phase?: "quick" | "exhaustive";
}
export interface ClassifyResponse {
  results: PaperResult[];
  // Cuántos venían de la caché de clasificaciones (sin llamar a Jev)
  cached?: number;
}

export interface AppConfig {
  backend: string;
  backend_error: string | null;
  default_limit: number;
  max_limit: number;
  // Tope de papers de la búsqueda exhaustiva
  exhaustive_max: number;
  batch_size: number;
  page_size: number;
  access_required: boolean;
  // Fuentes activas, en orden de preferencia
  sources: SourceName[];
  // Clave pública de Turnstile (null: sin captcha)
  turnstile_site_key: string | null;
  // Dominio público: la web servida desde *.workers.dev redirige a él (null: sin redirección)
  canonical_host: string | null;
  // Cloudflare Web Analytics: estadísticas sin cookies (null: desactivado)
  web_analytics_token: string | null;
}

/** Error de la API con código HTTP (para distinguir 4xx de 5xx en el cliente). */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}
